/**
 * Payment Service for Subscription Extension
 * Handles payment initialization and status checking
 */

import { API_BASE_URL as BASE_API_URL } from '../../../services/api';
import { getCurrentUserFromStore } from './auth';

// Use the base API URL from main services and append /subscriptions
const API_BASE_URL = `${BASE_API_URL}/subscriptions`;

// Store reference for payment service
let storeInstance = null;

/**
 * Set store instance for payment service
 */
export function setStoreForPaymentService(store) {
    storeInstance = store;
}

/**
 * Get authentication token from store using selector
 */
function getAuthToken() {
    const user = getCurrentUserFromStore(storeInstance);
    return user?.token || '';
}

/**
 * Payment Service Class
 */
class PaymentService {
    constructor() {
        this.baseURL = API_BASE_URL;
    }

    /**
     * Initialize payment for subscription
     * 
     * @param {string} subscriptionId - Subscription ID
     * @param {Object} options - Payment options
     * @param {string} options.email - Customer email
     * @param {string} options.phone - Customer phone number
     * @param {string} options.gateway - Payment gateway (default: 'pesapal')
     * @param {string} options.callbackUrl - Custom callback URL
     * @returns {Promise<Object>} Payment initialization data
     */
    async initializePayment(subscriptionId, options = {}) {
        const token = getAuthToken();
        
        const payload = {
            subscription_id: subscriptionId,
            payment_gateway: options.gateway || 'pesapal',
            customer_email: options.email,
            phone_number: options.phone,
            callback_url: options.callbackUrl
        };

        try {
            const response = await fetch(`${this.baseURL}/payment/initialize/`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`
                },
                body: JSON.stringify(payload)
            });

            if (!response.ok) {
                const error = await response.json().catch(() => ({ error: 'Payment initialization failed' }));
                throw new Error(error.error || `HTTP ${response.status}`);
            }

            const data = await response.json();
            
            return {
                paymentId: data.payment_id,
                paymentUrl: data.payment_url,
                gatewayPaymentId: data.gateway_payment_id,
                expiresAt: data.expires_at,
                amount: data.amount,
                currency: data.currency
            };
        } catch (error) {
            console.error('Payment initialization failed:', error);
            throw error;
        }
    }

    /**
     * Check payment status
     * 
     * @param {string} paymentId - Payment ID
     * @returns {Promise<Object>} Payment status
     */
    async checkPaymentStatus(paymentId) {
        const token = getAuthToken();

        try {
            const response = await fetch(`${this.baseURL}/payment/${paymentId}/status/`, {
                headers: {
                    'Authorization': `Bearer ${token}`
                }
            });

            if (!response.ok) {
                const error = await response.json().catch(() => ({ error: 'Failed to check payment status' }));
                throw new Error(error.error || `HTTP ${response.status}`);
            }

            const data = await response.json();
            
            return {
                paymentId: data.payment_id,
                status: data.status,
                amount: data.amount,
                currency: data.currency,
                gatewayProvider: data.gateway_provider,
                paymentMethod: data.payment_method,
                initiatedAt: data.initiated_at,
                paidAt: data.paid_at,
                expiresAt: data.expires_at
            };
        } catch (error) {
            console.error('Failed to check payment status:', error);
            throw error;
        }
    }

    /**
     * Poll payment status until success or timeout
     * 
     * @param {string} paymentId - Payment ID
     * @param {Object} options - Polling options
     * @param {number} options.interval - Polling interval in ms (default: 3000)
     * @param {number} options.maxAttempts - Max polling attempts (default: 20)
     * @returns {Promise<Object>} Final payment status
     */
    async pollPaymentStatus(paymentId, options = {}) {
        const interval = options.interval || 3000; // 3 seconds
        const maxAttempts = options.maxAttempts || 20; // 1 minute total
        
        for (let attempt = 0; attempt < maxAttempts; attempt++) {
            try {
                const status = await this.checkPaymentStatus(paymentId);
                
                // Return if payment completed (success or failed)
                if (status.status === 'success' || status.status === 'failed') {
                    return status;
                }
                
                // Wait before next poll
                await new Promise(resolve => setTimeout(resolve, interval));
            } catch (error) {
                console.error(`Payment polling attempt ${attempt + 1} failed:`, error);
                
                // Continue polling even if one request fails
                if (attempt < maxAttempts - 1) {
                    await new Promise(resolve => setTimeout(resolve, interval));
                } else {
                    throw error;
                }
            }
        }
        
        // Timeout - return last known status
        return this.checkPaymentStatus(paymentId);
    }

    /**
     * Get available payment gateways
     * 
     * @returns {Promise<Array>} List of available gateways
     */
    async getAvailableGateways() {
        // For now, return static list
        // TODO: Make API endpoint to check configured gateways
        return ['pesapal'];
    }
}

// Export singleton instance
export default new PaymentService();

